// src/utils/deviceDetection.js — browser side of the mobile-access policy.
// Collects signals that "Desktop site" mode cannot hide and applies the SAME rules
// as the server's deviceGuard.js (the server is the real enforcement; this gives an
// instant, friendly block screen and supplies the signals the server needs).
//
// Never rely on viewport width, CSS media queries about width, or the UA alone.

const MOBILE_UA_RE = /Android|iPhone|iPod|iPad|Mobile|Windows Phone|webOS|BlackBerry|Opera Mini|IEMobile|Silk|Kindle/i;
const DESKTOP_OS_RE = /Windows NT|CrOS/i;

const mq = (q) => {
    try { return !!(window.matchMedia && window.matchMedia(q).matches); } catch (e) { return false; }
};

export function collectDeviceSignals() {
    if (typeof window === 'undefined') return {};
    const nav = window.navigator || {};
    const scr = window.screen || {};
    const w = Number(scr.width) || 0, h = Number(scr.height) || 0;
    return {
        v: 1,
        ua: String(nav.userAgent || '').slice(0, 300),
        uaMobile: nav.userAgentData && typeof nav.userAgentData.mobile === 'boolean' ? nav.userAgentData.mobile : null,
        touch: Number(nav.maxTouchPoints) || 0,
        coarse: mq('(pointer: coarse)'),
        hoverNone: mq('(hover: none)'),
        orientationApi: typeof window.orientation !== 'undefined',
        screenMin: Math.min(w, h) || 0,      // PHYSICAL screen, not the (fakeable) viewport
        screenMax: Math.max(w, h) || 0
    };
}

export function classifyDevice(signals = collectDeviceSignals()) {
    const s = signals || {};
    const reasons = [];
    const screenMin = Number(s.screenMin) || 0;
    const touch = Number(s.touch) || 0;
    if (s.uaMobile === true) reasons.push('navigator.userAgentData.mobile');
    if (typeof s.ua === 'string' && MOBILE_UA_RE.test(s.ua)) reasons.push('navigator.userAgent: mobile');
    if (s.orientationApi === true) reasons.push('window.orientation present (mobile-only API)');
    if (/Macintosh/.test(s.ua || '') && touch > 1) reasons.push('iPadOS desktop mode (Mac UA + multi-touch)');
    if (s.coarse === true && s.hoverNone === true) reasons.push('primary input is touch (coarse pointer, no hover)');
    // Screen-size rules. screen.width/height are in CSS pixels, so a normal laptop with Windows
    // display scaling (e.g. a 1920x1080 panel at 150-200%) reports as small as 960x540 - that
    // must NOT look like a phone. So size only counts when the device also has a touchscreen
    // (every phone/tablet does), and Windows / ChromeOS laptops (incl. touchscreen ones such as
    // the Dell Latitude 7490) are exempt: phones and tablets never report those platforms,
    // not even in "Desktop site" mode (that gives a Linux/Mac-style UA).
    const desktopOS = DESKTOP_OS_RE.test(s.ua || '');
    if (touch > 0 && screenMin > 0 && screenMin < 450 && !desktopOS) reasons.push('phone-sized physical screen');
    if (touch > 0 && screenMin > 0 && screenMin < 768 && !desktopOS) reasons.push('touch device with small physical screen');
    return { mobile: reasons.length > 0, reasons };
}

export const isMobileDevice = () => classifyDevice().mobile;

// Value for the X-Device-Signals request header (ASCII-safe).
export const deviceSignalsHeader = () => encodeURIComponent(JSON.stringify(collectDeviceSignals()));